import { Stack, Tooltip, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { get, isEmpty, isNil, pickBy, truncate } from 'lodash';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import WhiteBoxContent from 'src/components/common/WhiteBoxContent';
import ExportDataTable from 'src/components/ExportData';
import { EXPORT_TABLE_TYPE, URL_EXPORT_MAPPING } from 'src/components/ExportData/config';
import CompanyInfoNoData from 'src/features/CompanyDetail/Common/CompanyInfoNoData';
import useCreditFlow from 'src/hooks/useCreditFlow';
import { LIST_ACTIONS, LIST_FEATURES } from 'src/utils/constants/featuresPoint';
import CustomTable from 'src/components/Table';
import { useQueryParams } from 'src/hooks/useQueryParam';
import { useQueryRuntimeConfig } from 'src/services/queries/useQueryRuntimeConfig';
import { useQueryCompanyTradeDataHSCodeList } from 'src/services/queries/useQueryTradeDataWithoutHSCode';
import { FORMAT_DATE_TIME, LIMIT_GET_LIST } from 'src/utils/constants/config';
import { KeyFilterChartTradeDataUnitBy } from 'src/utils/constants/enum';
import { getArrayDefault } from 'src/utils/helpers/arrayHelpers';
import { renderNumberOrDefault } from 'src/utils/helpers/stringHelpers';

const Top10HSCode = () => {
  const { id } = useParams();
  const {
    data: { data: runtimeConfig },
  } = useQueryRuntimeConfig();
  const queryParams = useQueryParams();
  const { end_date, start_date, keyword, viewedAsType } = queryParams;
  const [params, setParams] = useState(
    pickBy(
      {
        ...queryParams,
        offset: 0,
        limit: LIMIT_GET_LIST,
        start_date:
          start_date ?? dayjs(runtimeConfig?.MIN_TRADE_DATA_DATE, 'YYYY-MM-DD').format(FORMAT_DATE_TIME.yyyy_mm_dd),
        end_date:
          end_date ?? dayjs(runtimeConfig?.MAX_TRADE_DATA_DATE, 'YYYY-MM-DD').format(FORMAT_DATE_TIME.yyyy_mm_dd),
        id: id,
        sort_by: KeyFilterChartTradeDataUnitBy.value_usd,
        order: 'desc',
        keyword: keyword,
      },
      (value) => !isNil(value) && value !== '',
    ),
  );

  const [acceptCreditPayment, setAcceptCreditPayment] = useState(true);
  const { data, isFetching } = useQueryCompanyTradeDataHSCodeList(params);

  const [shouldShowBillableDataLoading, hasEnoughCredit] = useCreditFlow({
    response: data,
    featureInfo: {
      featureId: LIST_FEATURES.COMPANY_PROFILE_HS_CODE,
      action: LIST_ACTIONS.VIEW,
    },
    acceptCreditPayment,
    onSetAcceptCreditPayment: (value) => setAcceptCreditPayment(value),
  });
  const configColumn: ConfigCell<TypeCompanyTradeDataHSCodeList>[] = [
    {
      id: 'hs6_code',
      label: 'HS6 Code',
      disableSort: true,
      cell: (data: TypeCompanyTradeDataHSCodeList) => (
        <Typography variant="body2" color={'text.secondary'}>
          {data.hscode}
        </Typography>
      ),
    },
    {
      id: 'description',
      label: 'Description',
      disableSort: true,
      width: 400,
      cell: (data: TypeCompanyTradeDataHSCodeList) => (
        <Tooltip title={data.description}>
          <Typography variant="body2" color={'text.secondary'}>
            {truncate(data.description, {
              length: 100,
            })}
          </Typography>
        </Tooltip>
      ),
    },
    {
      id: 'bol',
      label: 'Bill of Lading',
      cell: (data: TypeCompanyTradeDataHSCodeList) => (
        <Typography variant="body2" color={'text.secondary'}>
          {data.bol}
        </Typography>
      ),
    },
    {
      id: 'weight',
      label: 'Weight (KG)',
      cell: (data: TypeCompanyTradeDataHSCodeList) => (
        <Typography variant="body2" color={'text.secondary'}>
          {renderNumberOrDefault(data.weight)}
        </Typography>
      ),
    },
    {
      id: 'quantity',
      label: 'Quantity',
      cell: (data: TypeCompanyTradeDataHSCodeList) => (
        <Typography variant="body2" color={'text.secondary'}>
          {renderNumberOrDefault(data.quantity)}
        </Typography>
      ),
    },
    {
      id: 'value_usd',
      label: 'Value (USD)',
      cell: (data: TypeCompanyTradeDataHSCodeList) => (
        <Typography variant="body2" color={'text.secondary'}>
          {renderNumberOrDefault(data.value_usd)}
        </Typography>
      ),
    },
  ];

  if (!shouldShowBillableDataLoading && !isFetching && isEmpty(data?.data)) {
    return (
      <CompanyInfoNoData
        title={hasEnoughCredit ? 'Trade Data Not Found' : 'Not Enough Credit'}
        content={
          hasEnoughCredit ? (
            <>
              Customs data may be restricted, incomplete, or obscured by alternative trading names or third-party
              logistics.
            </>
          ) : (
            'Can not access the data, please upgrade your plan'
          )
        }
      />
    );
  }

  return (
    <WhiteBoxContent>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5">
          Top 10 HS Code of{' '}
          <Typography display={'inline'} color={'secondary.main'} variant="h5">
            {keyword}
          </Typography>
        </Typography>
        <ExportDataTable
          fileName="ctd-hs-code"
          featureId={LIST_FEATURES.COMPANY_PROFILE_HS_CODE}
          endpoint={get(URL_EXPORT_MAPPING, EXPORT_TABLE_TYPE.CTD_HS_CODE)}
          paramsExport={params}
          totalRecordsAvailable={data?.total_records || 0}
        />
      </Stack>
      <CustomTable
        handleSort={(e) => {
          setParams({ ...params, sort_by: e.sortBy as KeyFilterChartTradeDataUnitBy, order: e.sort });
        }}
        onChangePage={(value) => {
          setParams({ ...params, offset: value });
        }}
        onChangePageSize={(value) =>
          setParams({
            ...params,
            ...{
              limit: value,
              offset: 0,
            },
          })
        }
        page={params.offset}
        pageSize={params.limit}
        totalRecords={data?.total_records}
        loading={shouldShowBillableDataLoading || isFetching}
        configColumn={configColumn}
        data={getArrayDefault(data?.data)}
        dataKey="hscode"
      />
    </WhiteBoxContent>
  );
};

export default Top10HSCode;
